import React, { useState } from "react";
import { fmtNum, getMeritos } from "../../../shared/utils";
import { sourceImage } from "../../../utils/source-image";

const MERIT_ICON_BY_CATEGORY = {
  horas: "clock",
  questões: "book",
  resumos: "doc",
  streak: "zap",
  pontos: "star",
  social: "users",
  desafios: "trophy",
};

const MERIT_ICON_BY_TYPE = {
  horas_total: "clock",
  horas_semana: "clock",
  questões_total: "book",
  questões_semana: "book",
  resumos_total: "doc",
  streak: "zap",
  checkpoints_total: "check",
  pontos_total: "star",
  indicacoes_total: "users",
  desafios_vencidos: "trophy",
  videoaula_total: "play",
};

function resolveMeritIconName(merit) {
  const explicit = String(merit?.icone || "").trim();
  if (explicit && explicit !== "*" && explicit.length > 1) return "custom";
  return (
    MERIT_ICON_BY_CATEGORY[merit?.cat] ||
    MERIT_ICON_BY_TYPE[merit?.tipo] ||
    "medal"
  );
}

function MeritGlyph({ name, size = 20, customText }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  if (name === "custom") return <span>{customText}</span>;
  if (name === "clock") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  }
  if (name === "book") {
    return (
      <svg {...common}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    );
  }
  if (name === "doc") {
    return (
      <svg {...common}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
      </svg>
    );
  }
  if (name === "zap") {
    return (
      <svg {...common}>
        <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
      </svg>
    );
  }
  if (name === "users") {
    return (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <path d="M20 8v6" />
        <path d="M23 11h-6" />
      </svg>
    );
  }
  if (name === "trophy") {
    return (
      <svg {...common}>
        <path d="M8 21h8" />
        <path d="M12 17v4" />
        <path d="M7 4h10v3a5 5 0 0 1-10 0z" />
        <path d="M5 7H3a2 2 0 0 0 2 2" />
        <path d="M19 7h2a2 2 0 0 1-2 2" />
      </svg>
    );
  }
  if (name === "check") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 12l3 3 5-6" />
      </svg>
    );
  }
  if (name === "play") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M10 8l6 4-6 4z" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 2l3 6 6 .9-4.5 4.4 1 6.2L12 17l-5.5 2.5 1-6.2L3 8.9 9 8z" />
    </svg>
  );
}

export default function Meritos({
  currentStudent,
  checkpoints,
  checkpointsComAutomaticos,
  batalhas,
  meritos: meritosBase,
  goto,
  viewingStudent,
  students,
  activities = [],
}) {
  const [cat, setCat] = useState("todas");
  const viewingStudentId =
    viewingStudent !== null && viewingStudent !== undefined
      ? Number(viewingStudent)
      : null;
  const aluno = viewingStudent
    ? students.find((s) => Number(s.id) === viewingStudentId)
    : currentStudent;
  if (!aluno) {
    return (
      <div className="cn">
        <h1 className="pt">
          <em>Meritos</em>
        </h1>
        <div className="es">
          <h3>Faca login para ver seus meritos</h3>
          <p style={{ marginTop: 14 }}>
            <button className="btn" onClick={() => goto("login-aluno")}>
              Entrar
            </button>
          </p>
        </div>
      </div>
    );
  }

  const resolveCatLabel = (cat) => {
    const num = Number(cat);
    if (!isNaN(num) && num > 0) {
      return activities.find((a) => a.id === num)?.nome || String(cat);
    }
    return String(cat);
  };

  const resolveCatTitle = (cat) => {
    const num = Number(cat);
    if (!isNaN(num) && num > 0) {
      const activity = activities.find((a) => a.id === num);
      if (activity?.descricao)
        return `${activity.nome} - ${activity.descricao}`;
      return activity?.nome || String(cat);
    }
    return String(cat);
  };

  const uniqueCats = [...new Set(meritosBase.map((m) => String(m.cat)))];
  const cats = ["todas", ...uniqueCats];

  const meritos = getMeritos(
    aluno.id,
    checkpoints,
    batalhas,
    meritosBase,
    activities,
    checkpointsComAutomaticos,
  );

  const filtrados =
    cat === "todas" ? meritos : meritos.filter((m) => String(m.cat) === cat);
  const conquistados = meritos.filter((m) => m.conquistado).length;
  const totalMeritos = meritos.length;
  const percentualConquista =
    totalMeritos > 0 ? Math.round((conquistados / totalMeritos) * 100) : 0;
  return (
    <div className="cn">
      <h1 className="pt">
        <em>Meritos</em>
      </h1>
      <div className="ps">Conquiste todos os {totalMeritos} meritos!</div>
      <div
        style={{
          background: "rgba(255,255,255,0.1)",
          borderRadius: 12,
          padding: "14px",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: "#FFF" }}>
            {conquistados} / {totalMeritos} conquistados
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#FFC76B" }}>
            {percentualConquista}%
          </span>
        </div>
        <div
          style={{
            height: 6,
            background: "rgba(255,255,255,0.15)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: 6,
              background: "#FFC76B",
              borderRadius: 999,
              width: `${percentualConquista}%`,
              transition: "width .5s",
            }}
          />
        </div>
      </div>
      <div
        className="tabs"
        style={{ flexDirection: "row", flexWrap: "wrap", gap: "4px" }}
      >
        {cats.map((c) => (
          <button
            key={c}
            className={cat === c ? "a" : ""}
            onClick={() => setCat(c)}
            style={{
              flex: "0 1 auto",
              textTransform: "capitalize",
              fontSize: 11,
              whiteSpace: "nowrap",
              padding: "10px 6px",
            }}
            title={resolveCatTitle(c)}
          >
            {resolveCatLabel(c)}
          </button>
        ))}
      </div>
      <div className="meritos-grid">
        {filtrados.map((m) => {
          const iconColor = m.cor || "#4FB3A9";
          const iconName = resolveMeritIconName(m);
          const explicit = String(m.icone || "").trim();
          const pct =
            m.meta > 0 ? Math.min(100, Math.round((m.prog / m.meta) * 100)) : 0;
          return (
            <div
              key={m.id}
              className={`merito-card ${m.conquistado ? "conquistado" : "off"}`}
            >
              {m.conquistado && (
                <div
                  style={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#FFC76B",
                  }}
                >
                  *
                </div>
              )}
              {m.img ? (
                <img
                  src={sourceImage(m.img)}
                  alt={m.titulo}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 8,
                    objectFit: "cover",
                    margin: "0 auto 6px",
                    display: "block",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 8,
                    background: iconColor,
                    margin: "0 auto 6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    color: "#FFFFFF",
                  }}
                >
                  <MeritGlyph name={iconName} customText={explicit || "*"} />
                </div>
              )}
              <div
                className="merito-titulo"
                style={{ color: m.conquistado ? "#1F4754" : "#6B8590" }}
              >
                {m.titulo}
              </div>
              <div className="merito-desc">{m.desc}</div>
              <div className="merito-prog">
                <div
                  className="merito-prog-fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="merito-pct">
                {fmtNum(m.prog, 1)} / {fmtNum(m.meta, 1)} ({pct || 0}%)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
